import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArcStack, ArcToolbar } from '@src/core/ui';
import { useDisclosure } from '@src/core/hooks';
import { MissionIdParams } from '@src/core/entities';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { ChecklistList, ChecklistAgreement, ChecklistGamepadMessage, ChecklistActions } from './components';
import { buildChecklistItems, checklistItems, SIDEBAR_TITLE } from './constants';
import { safeDistanceLabel } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';

const initialCheckedItems = checklistItems.reduce((acc, item) => {
  acc[item.id] = false;
  return acc;
}, {} as Record<string, boolean>);

const ChecklistSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { missionId } = useParams<MissionIdParams>();
  const { data } = useGetOrderMissionQuery(missionId);

  // Render the checklist with the unit-aware clearance label; re-renders on a live
  // unit switch (UNITS Standard R1/R5). Static `checklistItems` (ids) drives state.
  const unitType = useUnitType();
  const items = React.useMemo(() => buildChecklistItems(safeDistanceLabel(unitType)), [unitType]);

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(initialCheckedItems);
  const { open: isAgree, toggle: handleAgreeToggle, show: setAgreeTrue, hide: setAgreeFalse } = useDisclosure(false);
  
  const droneSerialNumber = data?.drone?.serialNumber;

  const allChecked = Object.values(checkedItems).every(Boolean) && isAgree;

  // Sync agreement checkbox when all items are manually checked/unchecked
  useEffect(() => {
    const allItemsChecked = Object.values(checkedItems).every(Boolean);
    if (allItemsChecked && !isAgree) {
      setAgreeTrue();
    } else if (!allItemsChecked && isAgree) {
      setAgreeFalse();
    }
  }, [checkedItems, isAgree, setAgreeTrue, setAgreeFalse]);

  const handleItemCheck = (id: string) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAgreeToggleWithItems = () => {
    const newAgreeState = !isAgree;
    handleAgreeToggle();
    
    const allItemIds = checklistItems.map(item => item.id);
    const newCheckedItems = allItemIds.reduce((acc, id) => {
      acc[id] = newAgreeState;
      return acc;
    }, {} as Record<string, boolean>);
    
    setCheckedItems(newCheckedItems);
  };

  return (
    <ArcStack spacing={3} height='100%' justifyContent='space-between'>
      <ArcStack spacing={2}>
        <ArcToolbar 
          title={SIDEBAR_TITLE} 
          goBack={() => navigate(-1)} 
        />

        <ArcStack spacing={1}>
          <ChecklistList
            items={items}
            checkedItems={checkedItems}
            onItemToggle={handleItemCheck}
          />

          <ChecklistAgreement
            checked={isAgree}
            onToggle={handleAgreeToggleWithItems}
          />

        </ArcStack>
      </ArcStack>

      <ArcStack spacing={2} alignItems='center'>
        <ChecklistGamepadMessage serialNumber={droneSerialNumber} />
        <ChecklistActions allChecked={allChecked} />
      </ArcStack>
    </ArcStack>
  );
};

export default ChecklistSidebar;